import { httpClient } from './httpClient';

export type ObjectiveType =
  | 'CHRONOLOGY'
  | 'STANDARD_OF_CARE'
  | 'INCONSISTENCY_DETECTION'
  | 'GAP_ANALYSIS'
  | 'CAUSATION_ANALYSIS'
  | 'COMPARATIVE_ANALYSIS'
  | 'GUIDELINE_COMPLIANCE'
  | 'EXPERT_OPINION_SUPPORT'
  | 'COURT_BUNDLE_PREPARATION'
  | 'ISSUE_FOCUSED_SUMMARY';

export type ObjectiveStatus = 'UP_TO_DATE' | 'NEEDS_REGENERATION' | 'PROCESSING' | 'ERROR';

export type OutputType = 'CHRONOLOGY' | 'ANALYSIS' | 'REPORT' | 'FINDINGS' | 'COMPARISON';

export interface Objective {
  id: string;
  caseId: string;
  name: string;
  type: ObjectiveType;
  description?: string;
  configuration?: Record<string, any>;
  patientIds: string[];
  timeframeStart?: string;
  timeframeEnd?: string;
  documentTypes: string[];
  status: ObjectiveStatus;
  lastGeneratedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Citation {
  documentId: string;
  documentName: string;
  pageNumber?: number;
  excerpt?: string;
}

export interface ChronologyEvent {
  id: string;
  date: string;
  time?: string;
  title: string;
  description: string;
  category: string;
  citations: Citation[];
}

export interface AnalysisSection {
  id: string;
  title: string;
  content: string;
  severity?: 'info' | 'warning' | 'critical';
  citations: Citation[];
  subsections?: AnalysisSection[];
}

export interface ChronologyOutput {
  type: 'CHRONOLOGY';
  summary: string;
  events: ChronologyEvent[];
}

export interface AnalysisOutput {
  type: 'ANALYSIS' | 'REPORT' | 'FINDINGS' | 'COMPARISON';
  summary: string;
  sections: AnalysisSection[];
  conclusion?: string;
}

export type ObjectiveOutput = ChronologyOutput | AnalysisOutput;

export interface OutputResponse {
  id: string;
  objectiveId: string;
  type: OutputType;
  content: ObjectiveOutput;
  version: number;
  generatedAt: string;
  createdAt: string;
}

class ObjectiveService {
  async getObjectives(caseId: string): Promise<Objective[]> {
    return await httpClient.get<Objective[]>(`/cases/${caseId}/objectives`);
  }

  async getOutput(caseId: string, objectiveId: string): Promise<OutputResponse> {
    return await httpClient.get<OutputResponse>(`/cases/${caseId}/objectives/${objectiveId}/output`);
  }
}

export default new ObjectiveService();
